import { useNavigate } from '@tanstack/react-router';
import type { PaginationState, RowSelectionState } from '@tanstack/react-table';
import { ChevronDown, ChevronUp, Filter, Globe, Loader2, Search, Funnel, X } from 'lucide-react';
import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ActorStringLabel } from '~/components/common/actor-string-label';
import { PLATFORM_LOGO, SourceDestinationChip } from '~/components/common/chips';
import { DataTable } from '~/components/common/data-table/data-table';
import type { DataTableColumn } from '~/components/common/data-table/data-table.types';
import { FormDatePicker } from '~/components/common/forms/date-picker/form-date-picker';
import type { FormSelectOption } from '~/components/common/forms/select/form-select';
import { FormSelect } from '~/components/common/forms/select/form-select';
import { PremiumBadge } from '~/components/common/premium-badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { BatchChildRuns } from '~/features/catalog-operations/components/batch-child-runs';
import { StatusChip } from '~/features/catalog-operations/components/chips';
import { EnrichmentJobArchiveControl } from '~/features/catalog-operations/components/enrichment-job-archive-control';
import {
  useBatches,
  useJobStatusCounts,
} from '~/features/catalog-operations/hooks/use-catalog-batches';
import { useScheduledBatchResume } from '~/features/catalog-operations/hooks/use-scheduled-batch-resume';
import { useSetEnrichmentJobsArchivedBatch } from '~/features/catalog-operations/hooks/use-set-enrichment-jobs-archived-batch';
import { useCatalogOperationsStore } from '~/features/catalog-operations/store/catalog-operations.store';
import type { ConnectorCodeWire } from '~/features/catalog-operations/types/catalog-operations.api.types';
import type {
  BatchRecord,
  BatchTab,
  JobStatusCounts,
} from '~/features/catalog-operations/types/catalog-operations.types';
import {
  batchFlowIcons,
  resolveBatchChipStatus,
} from '~/features/catalog-operations/utils/batch-flow-icons';
import {
  describeChildRuns,
  resolveBatchStatusDisplay,
} from '~/features/catalog-operations/utils/batch-status-display';
import { lastActionLabelKey } from '~/features/catalog-operations/utils/last-action';
import { ArchiveBulkActions } from '~/features/list-archive/components/archive-bulk-actions';
import { ArchiveViewSwitch } from '~/features/list-archive/components/archive-view-switch';
import { useCanManageArchive } from '~/features/list-archive/hooks/use-can-manage-archive';
import { cn } from '~/utils/cn';
import {
  endOfLocalDayISO,
  endOfTodayISO,
  formatDate,
  formatRelative,
  jsDateToIsoDate,
  shiftIsoDate,
  todayIsoDate,
} from '~/utils/date';
import { splitDisplayLabel } from '~/utils/split-display-label';

/** True for a TRIAL row whose trial stage has reached `COMPLETED` — the
 *  backend only sets this once the full-catalog run has finished. These rows
 *  show a "Completed" status label and route to the generic batch detail
 *  page instead of the trial wizard. */
function isCompletedTrialFullCatalog(row: BatchRecord): boolean {
  return row.source === 'TRIAL' && row.trialStage === 'COMPLETED';
}

/** Status chip + sub-line. With the server-reconciled `batchStatus` the chip reflects the whole
 *  batch (root + retries); without it the row falls back to the legacy status logic. */
function BatchStatusCell({ row }: { row: BatchRecord }) {
  const display = resolveBatchStatusDisplay({
    batchStatus: row.batchStatus,
    fallbackStatus: row.status,
    retryAttempts: row.retryAttempts,
  });
  // List rows rely on the list API's costGate only — no per-row estimate fetch.
  const costWaiting = row.costGate
    ? { waitingSince: row.costGate.waitingSince, workflowId: row.costGate.workflowId }
    : null;
  if (!display) {
    return (
      <StatusChip
        testId={`catalog-ops-batch-status-badge-${row.id}`}
        status={resolveBatchChipStatus(row.status, row.source)}
        {...(isCompletedTrialFullCatalog(row) ? { label: 'Completed' } : {})}
        costWaiting={costWaiting}
      />
    );
  }
  const excluded = row.productsExcluded ?? 0;
  return (
    <div className="flex flex-col items-start gap-1">
      <StatusChip
        testId={`catalog-ops-batch-status-badge-${row.id}`}
        status={resolveBatchChipStatus(display.status, row.source)}
        label={isCompletedTrialFullCatalog(row) ? 'Completed' : display.label}
        {...(display.className ? { className: display.className } : {})}
        costWaiting={costWaiting}
      />
      <span
        data-testid={`catalog-ops-batch-status-sub-${row.id}`}
        className="text-muted-foreground text-xs"
      >
        {row.batchStatus === 'PAUSED' && row.pausedBy
          ? `paused by ${row.pausedBy}`
          : describeChildRuns(row.retryAttempts)}
        {excluded > 0 ? ` · ${excluded.toLocaleString()} excluded` : ''}
      </span>
    </div>
  );
}

/** Needs Attention count; a link into the detail page's Needs Attention drawer when non-zero. */
function NeedsAttentionCell({
  row,
  onOpen,
}: {
  row: BatchRecord;
  onOpen: (row: BatchRecord) => void;
}) {
  const count = row.needsAttention;
  if (count === undefined || count === null) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  if (count === 0) return <span className="text-sm font-medium tabular-nums">0</span>;
  return (
    <button
      type="button"
      data-testid={`catalog-ops-batch-needs-attention-${row.id}`}
      className="text-warning cursor-pointer text-sm font-semibold tabular-nums hover:underline"
      onClick={(e) => {
        e.stopPropagation();
        onOpen(row);
      }}
    >
      {count.toLocaleString()} ›
    </button>
  );
}

/** Batch ID column label — prefers the human-readable `name` when the
 *  backend has populated it, falling back to the shortened batch id. */
function resolveBatchDisplayLabel(row: BatchRecord): string {
  return row.name.trim() !== '' ? row.name : splitDisplayLabel(row.batchId, '-').displayLabel;
}

/** Maps the raw `enrichmentType` enum (e.g. `GMC_ADS`, `META_ADS`,
 *  `GOOGLE_MERCHANT`, `SEO_AEO`) to a friendly label + icon kind. Substring
 *  matching covers both the use-case and connector-code forms. */
function resolveEnrichmentTypeDisplay(value: string): {
  label: string;
  icon: 'google' | 'meta' | 'globe';
} {
  const normalized = value.toUpperCase();
  if (normalized.includes('GMC') || normalized.includes('GOOGLE')) {
    return { label: 'Google Ads', icon: 'google' };
  }
  if (normalized.includes('META') || normalized.includes('FACEBOOK')) {
    return { label: 'Meta Ads', icon: 'meta' };
  }
  if (normalized.includes('SEO') || normalized.includes('AEO')) {
    return { label: 'SEO / AEO', icon: 'globe' };
  }
  if (normalized.includes('SHOPIFY')) {
    return { label: 'Shopify Ads', icon: 'globe' };
  }
  // Generic title-case fallback so unknown enums still render readably.
  const label = value
    .toLowerCase()
    .split('_')
    .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
  return { label, icon: 'globe' };
}

/** Pill rendered in the Enrichment Type column — mirrors the design's grey
 *  rounded chip with a brand glyph + label. */
function EnrichmentTypeChip({ value }: { value: string }) {
  const { label, icon } = resolveEnrichmentTypeDisplay(value);
  return (
    <span
      data-testid="catalog-ops-batch-enrichment-type-badge"
      className="bg-muted inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium"
    >
      {icon === 'google' && (
        <img src={PLATFORM_LOGO.Google} alt="" className="h-4 w-4 shrink-0 object-contain" />
      )}
      {icon === 'meta' && (
        <img src={PLATFORM_LOGO.Meta} alt="" className="h-4 w-4 shrink-0 object-contain" />
      )}
      {icon === 'globe' && (
        <Globe className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const BATCH_TABS: BatchTab[] = [
  { value: 'all', label: 'All Batches' },
  { value: 'in-progress', label: 'In Progress', statusFilter: 'in-progress' },
  { value: 'delivered', label: 'Delivered', statusFilter: 'delivered' },
  { value: 'failed', label: 'Failed', statusFilter: 'failed' },
];

/** Maps a tab value to its `JobStatusCounts` field. Mirrors `uiTabToWireStatus`
 *  1:1 — a tab's badge must match the count of rows that tab actually filters
 *  to, so `in-progress` only reflects `inProgress` (not pending/reset/etc,
 *  which today only ever surface under "All"). Returns undefined for tabs
 *  with no matching field, which hides the pill. */
function countForTab(counts: JobStatusCounts | undefined, tabValue: string): number | undefined {
  if (!counts) return undefined;
  switch (tabValue) {
    case 'all':
      return counts.all;
    case 'in-progress':
      return counts.inProgress;
    case 'delivered':
      return counts.completed;
    case 'failed':
      return counts.failed;
    default:
      return undefined;
  }
}

/** Page size sent to the listing endpoint. Single-fetch pagination keeps the
 *  DataTable's internal paging usable without wiring server-page controls. */
const BATCHES_PAGE_SIZE = 100;

/** Maps a trial stage to its zero-based wizard step index. Index 0 (empty
 *  string) covers null/unknown stages so the wizard always opens at step 0
 *  when the stage can't be determined. */
const TRIAL_STAGES = [
  '',
  'CONTENT_SCORING',
  'SAMPLE_ENRICHMENT',
  'FULL_CATALOG',
  'COMPLETED',
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Tabs locked behind premium for trial users — these values must match BATCH_TABS. */
const PREMIUM_LOCKED_TABS = new Set(['delivered', 'failed']);

/** i18n key for each premium-locked tab's crown tooltip — keys must match PREMIUM_LOCKED_TABS. */
const PREMIUM_TAB_TOOLTIP_KEY: Record<string, string> = {
  delivered: 'batchTable.deliveredTooltip',
  failed: 'batchTable.failedTooltip',
};

/**
 * Tabbed batch table for the Catalog Operations page.
 * Drives the table from `useBatches` — server-side filtering by tab/status
 * and search; DataTable handles in-page sorting and pagination over the
 * fetched window.
 * When `isTrial` is true, the Delivered and Failed tabs are premium-locked
 * (disabled with a crown icon).
 */
export function CatalogBatchTable({ isTrial = false }: { isTrial?: boolean }) {
  const { t } = useTranslation('catalog-operations');
  // Shared List Archive copy (view switch, empty state) lives in the common ns.
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { resume, resumingId } = useScheduledBatchResume();
  // Super / org admins get the Active | Archived switch, the row toggle and
  // multi-select bulk archive.
  const canManageArchive = useCanManageArchive();
  const jobsBatch = useSetEnrichmentJobsArchivedBatch();
  // Bulk-archive selection, owned here rather than by DataTable so the count, the
  // action and Clear render inline in the toolbar row beside the search. The
  // table's own bulk-action slot would open a second row the moment a row is
  // ticked, shifting everything below it.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // Tab/search/filters/pagination/sort all live in a store (not local state)
  // so the listing survives navigating into a batch's detail/enrichment flow
  // and back — see catalog-operations.store.ts.
  const activeTab = useCatalogOperationsStore((s) => s.activeTab);
  const setActiveTab = useCatalogOperationsStore((s) => s.setActiveTab);
  const view = useCatalogOperationsStore((s) => s.view);
  const setView = useCatalogOperationsStore((s) => s.setView);
  // `search` is the committed value (fires the API request); `searchDraft`
  // tracks the in-flight input and only commits on Enter or Clear — matches
  // the product-listing UX. The draft itself is transient UI state, not persisted.
  const search = useCatalogOperationsStore((s) => s.search);
  const setSearch = useCatalogOperationsStore((s) => s.setSearch);
  // Seeded from the persisted committed search so the visible input reflects
  // an already-applied search after returning to the listing.
  const [searchDraft, setSearchDraft] = useState(search);
  const [filterOpen, setFilterOpen] = useState(false);
  /** Committed filter-drawer selections. `appliedFilterCount` surfaces as a
   *  primary-blue badge on the Filter button. */
  const filters = useCatalogOperationsStore((s) => s.filters);
  const applyFilters = useCatalogOperationsStore((s) => s.applyFilters);
  const { connectorCode, useCase, fromDate, toDate, coverage, source, appliedFilterCount } =
    filters;
  const pagination = useCatalogOperationsStore((s) => s.pagination);
  const setPagination = useCatalogOperationsStore((s) => s.setPagination);
  const sorting = useCatalogOperationsStore((s) => s.sorting);
  const setSorting = useCatalogOperationsStore((s) => s.setSorting);

  // Committed drawer filters, spread only when set — exactOptionalPropertyTypes
  // forbids assigning `undefined` to the optional params.
  const drawerFilters = {
    ...(connectorCode ? { connectorCode } : {}),
    ...(useCase ? { useCase } : {}),
    ...(fromDate ? { fromDate } : {}),
    ...(toDate ? { toDate } : {}),
    ...(coverage ? { coverage } : {}),
    ...(source ? { source } : {}),
  };
  // Archived view adds `archived=ARCHIVED` to every list / count request. The
  // active view sends nothing (server default hides archived rows), keeping
  // today's params — and cache slots — byte-identical.
  const archivedParams = view === 'archived' ? { archived: 'ARCHIVED' as const } : {};

  const { data, isLoading, isError, isPlaceholderData, refetch } = useBatches({
    tab: activeTab,
    search,
    page: 0,
    size: BATCHES_PAGE_SIZE,
    ...drawerFilters,
    ...archivedParams,
  });
  // Same non-status filters as `useBatches`, minus `tab`/`page`/`size` — kept
  // in sync so the tab badges never drift from the list beneath them.
  const { data: statusCounts } = useJobStatusCounts({
    search,
    ...drawerFilters,
    ...archivedParams,
  });
  // Archived-set counts feed the view switch's badge — read `.all` (per the
  // contract; NOT `.archived`, which counts the Stop action's terminal status).
  // Admin-only; in the Archived view it shares the tab-counts key, so one request.
  const { data: archivedCounts } = useJobStatusCounts(
    { search, ...drawerFilters, archived: 'ARCHIVED' },
    { enabled: canManageArchive },
  );
  // Spread `count` in only when defined — exactOptionalPropertyTypes forbids
  // explicitly assigning `undefined` to the optional property.
  const tabsWithCounts = useMemo<BatchTab[]>(
    () =>
      BATCH_TABS.map((tab) => {
        const count = countForTab(statusCounts, tab.value);
        return count !== undefined ? { ...tab, count } : tab;
      }),
    [statusCounts],
  );
  // Read-only KPI counts for the decoupled-delivery sub-states. Surfaced as small
  // indicators rather than filter tabs — `/jobs` can't filter by `pipelineStatus`,
  // and both stay visible under the "All" tab. Absent counts (older backend) read 0.
  const deliveringCount = statusCounts?.delivering ?? 0;
  const rejectedCount = statusCounts?.rejected ?? 0;

  // Columns depend on `resumingId` to render a per-row loading spinner
  // while the Meta batch resume fetch is in flight.
  const columns = useMemo<DataTableColumn<BatchRecord>[]>(() => {
    // Needs Attention value → batch detail with the Needs Attention drawer open.
    const openNeedsAttention = (row: BatchRecord) => {
      void navigate({
        to: '/catalog-operations/$batchId',
        params: { batchId: row.id },
        search: { tab: 'delivery-health', na: 1 },
      });
    };
    const base: DataTableColumn<BatchRecord>[] = [
      {
        id: 'batchId',
        header: 'Batch ID',
        accessorFn: (row) => resolveBatchDisplayLabel(row),
        cell: (row) => (
          <span className="inline-flex items-center gap-2 text-sm font-medium">
            {resumingId === row.batchId && (
              <Loader2
                aria-hidden="true"
                data-testid={`catalog-ops-batch-resume-loading-${row.id}`}
                className="text-primary h-3.5 w-3.5 animate-spin"
              />
            )}
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span data-testid={`catalog-ops-batch-name-${row.id}`}>
                  {resolveBatchDisplayLabel(row)}
                </span>
              </TooltipTrigger>
              <TooltipContent>{row.batchId}</TooltipContent>
            </Tooltip>
          </span>
        ),
        className: 'min-w-[200px]',
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: (row) => <BatchStatusCell row={row} />,
        className: 'min-w-[130px]',
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorFn: (row) => row.createdAt,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {row.createdAt ? formatDate(row.createdAt) : '—'}
          </span>
        ),
        className: 'min-w-[140px]',
      },
      {
        id: 'createdBy',
        header: 'Created by',
        accessorFn: (row) => row.createdBy ?? '',
        cell: (row) => <CreatedByCell row={row} />,
        className: 'min-w-[170px]',
      },
      {
        id: 'lastAction',
        header: 'Last action',
        accessorFn: (row) => row.lastActionAt ?? '',
        cell: (row) => <LastActionCell row={row} />,
        className: 'min-w-[170px]',
      },
      {
        id: 'deliveredAt',
        header: 'Delivered',
        accessorFn: (row) => row.deliveredAt ?? '',
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {row.deliveredAt ? formatDate(row.deliveredAt) : '—'}
          </span>
        ),
        className: 'min-w-[140px]',
      },
      {
        id: 'enrichmentType',
        header: 'Enrichment Type',
        accessorFn: (row) => row.enrichmentType,
        cell: (row) => <EnrichmentTypeChip value={row.enrichmentType} />,
        className: 'min-w-[170px]',
      },
      {
        id: 'productCount',
        header: 'Products',
        accessorFn: (row) => row.productCount,
        cell: (row) => (
          <span
            data-testid={`catalog-ops-batch-product-count-${row.id}`}
            className="text-sm font-medium"
          >
            {row.productCount.toLocaleString()}
          </span>
        ),
        className: 'min-w-[110px] text-center',
      },
      {
        id: 'needsAttention',
        header: 'Needs Attention',
        accessorFn: (row) => row.needsAttention ?? 0,
        cell: (row) => <NeedsAttentionCell row={row} onOpen={openNeedsAttention} />,
        className: 'min-w-[130px] text-center',
      },
      {
        id: 'platforms',
        header: 'Source → Destination',
        cell: (row) => (
          <SourceDestinationChip
            sourcePlatform={row.sourcePlatform}
            destinationPlatform={row.destinationPlatform}
            {...batchFlowIcons(row.source)}
            testId={`catalog-ops-batch-platforms-${row.id}`}
          />
        ),
        className: 'min-w-[200px]',
      },
      {
        id: 'coverage',
        header: 'Coverage',
        accessorFn: (row) => row.coveragePercent,
        cell: (row) => (
          <div className="flex min-w-30 items-center gap-2">
            <div className="bg-neutral-30 h-1.5 flex-1 overflow-hidden rounded-full">
              <div
                className="bg-accent-main h-full rounded-full transition-all"
                style={{ width: `${String(row.coveragePercent)}%` }}
              />
            </div>
            <span
              data-testid={`catalog-ops-batch-coverage-value-${row.id}`}
              className="w-9 text-right text-xs font-medium tabular-nums"
            >
              {String(row.coveragePercent)}%
            </span>
          </div>
        ),
        className: 'min-w-[160px]',
      },
    ];
    // Admin-only trailing Archive / Unarchive toggle. The control owns its
    // mutation (and the Stop / Reconcile dialog a live run opens), so the column
    // definitions stay memoizable. Never pre-disabled: the server decides.
    if (canManageArchive) {
      base.push({
        id: 'actions',
        header: '',
        accessorFn: () => null,
        enableSorting: false,
        cell: (row) => (
          <EnrichmentJobArchiveControl
            jobId={row.id}
            archivedAt={row.archivedAt}
            className="text-muted-foreground hover:text-foreground h-8 w-8"
            testId={`catalog-ops-batch-archive-button-${row.id}`}
          />
        ),
        className: 'w-[56px] text-right',
      });
    }
    return base;
  }, [resumingId, canManageArchive, navigate]);

  function commitSearch(value: string) {
    setSearch(value);
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSearch(searchDraft.trim());
    }
  }

  function handleSearchClear() {
    setSearchDraft('');
    commitSearch('');
  }

  /** Dims the row being resumed and blocks pointer events on all others
   *  to prevent concurrent Meta batch resume operations. */
  function getRowClassName(row: BatchRecord): string | undefined {
    // Only parents with retry children can expand: hide the generic chevron on the rest.
    const expander =
      (row.retryAttempts ?? 0) > 0 ? undefined : "[&_button[aria-label='Expand_row']]:invisible";
    if (!resumingId) return expander;
    return cn(
      expander,
      row.batchId === resumingId
        ? 'opacity-60 cursor-wait'
        : 'cursor-not-allowed pointer-events-none',
    );
  }

  /** "Open ›" on a child run → batch detail, Runs tab, that run's drawer open. */
  function openChildRun(jobId: string, runId: string): void {
    void navigate({
      to: '/catalog-operations/$batchId',
      params: { batchId: jobId },
      search: { tab: 'run-details', run: runId },
    });
  }

  /** Routes row clicks to the correct destination:
   *  - TRIAL rows that failed → generic batch detail page (no wizard step to resume)
   *  - TRIAL rows that finished the full-catalog run → generic batch detail page
   *  - TRIAL rows at any other active/pending stage → trial enrichment wizard
   *  - In-progress FEED_AUDIT rows → Feed Audit enrichment wizard
   *  - In-progress Meta / Google Ads rows → scheduled batch resume funnel
   *  - Everything else → generic batch detail page */
  function handleRowClick(row: BatchRecord): void {
    // FEED_AUDIT runs resume into their own enrichment wizard — the scheduled
    // batch resume funnel below has no route for this source and 404s.
    if (row.source === 'FEED_AUDIT' && row.status === 'in-progress') {
      void navigate({ to: '/feed-audit/$id', params: { id: row.id } });
      return;
    }
    if (row.source === 'TRIAL') {
      if (row.status === 'failed' || isCompletedTrialFullCatalog(row)) {
        void navigate({
          to: '/catalog-operations/$batchId',
          params: { batchId: row.trialId ?? '' },
        });
        return;
      }
      const platform = row.enrichmentType.toUpperCase().includes('META') ? 'meta' : 'google';
      void navigate({
        to: '/trial/$platform/enrichment/$id',
        params: { platform, id: row.batchId },
        search: { step: (TRIAL_STAGES as readonly string[]).indexOf(row.trialStage ?? '') },
      });
      // Other trial stages (SAMPLE_ENRICHMENT, FULL_CATALOG, COMPLETED)
      // will be handled once wizard stages are built.
      return;
    }
    // In-progress scheduled batches resume into the staged funnel for their
    // platform; Meta rows carry a META/FACEBOOK enrichment type, everything else
    // (GMC_ADS) resumes into the Google funnel.
    if (row.status === 'in-progress') {
      const platform = row.enrichmentType.toUpperCase().includes('META') ? 'meta' : 'google';
      void resume(row.batchId, platform);
      return;
    }
    void navigate({
      to: '/catalog-operations/$batchId',
      params: { batchId: row.id },
    });
  }

  // `keepPreviousData` serves the previous tab's rows while a newly-selected tab
  // fetches (`isPlaceholderData`), which otherwise flashes e.g. in-progress
  // batches under the Delivered tab until the request resolves. Track the
  // tab + view the shown rows were fetched for (recorded only when we hold fresh,
  // non-placeholder data) and skeleton the body while it disagrees with the
  // current selection. Same-selection changes (search/filter) keep previous data
  // as before — no flicker there.
  const selectionKey = `${activeTab}|${view}`;
  const [dataKey, setDataKey] = useState(selectionKey);
  if (!isPlaceholderData && dataKey !== selectionKey) setDataKey(selectionKey);
  const isStaleTabData = isPlaceholderData && dataKey !== selectionKey;
  const showTableSkeleton = isLoading || isStaleTabData;

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const activeTabDef = tabsWithCounts.find((t) => t.value === activeTab) ?? tabsWithCounts[0];
  /** Guards against a persisted `pageIndex` outliving a shrunk result set —
   *  e.g. the user was on page 3, navigated away, and a later visit's
   *  filtered/searched window has fewer rows. Clamps only the value handed
   *  to DataTable; the store keeps the user's original intent so paging can
   *  "un-clamp" again once the result set grows back. */
  const displayPagination = useMemo<PaginationState>(() => {
    const pageCount = Math.max(1, Math.ceil(rows.length / pagination.pageSize));
    return pagination.pageIndex >= pageCount
      ? { ...pagination, pageIndex: pageCount - 1 }
      : pagination;
  }, [pagination, rows.length]);
  // Disable search and filter only when the list is natively empty — i.e. the
  // API returned 0 results with no search term or drawer filters active. When
  // filters are applied, the controls must stay enabled so the user can clear them.
  const isNativelyEmpty =
    !isLoading &&
    !isError &&
    data !== undefined &&
    total === 0 &&
    search === '' &&
    appliedFilterCount === 0;

  // Rows currently ticked on this page; ids that fell out of `rows` (archived
  // away, or a tab/view switch) simply stop counting.
  const selectedRows = rows.filter((r) => rowSelection[r.id]);
  const clearSelection = () => {
    setRowSelection({});
  };
  return (
    <>
      <Card data-testid="catalog-ops-batch-table-card">
        <CardContent className="p-0">
          <Tabs
            data-testid="catalog-ops-batch-tabs-root"
            value={activeTab}
            onValueChange={(v) => {
              // Store's setActiveTab already clears the committed search and
              // resets pagination — only the local draft needs clearing here.
              setActiveTab(v);
              setSearchDraft('');
              clearSelection();
            }}
          >
            {/* Tab bar — status tabs plus, for admins, the Active | Archived view
                switch. The two compose: tabs filter inside the selected view. */}
            <div className="flex items-center gap-4 border-b px-6 py-4">
              <TabsList
                data-testid="catalog-ops-batch-tabs"
                className="min-w-0 flex-1 justify-evenly gap-1"
              >
                {tabsWithCounts.map((tab) => {
                  const isPremiumLocked = isTrial && PREMIUM_LOCKED_TABS.has(tab.value);
                  return (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      data-testid={`catalog-ops-batch-tabs-${tab.value}`}
                      disabled={isPremiumLocked}
                      // disabled:opacity-100 prevents the trigger's opacity cascade from
                      // fading the PremiumBadge; only the label text is dimmed instead.
                      className={cn(
                        'w-full disabled:opacity-100',
                        isPremiumLocked ? 'cursor-not-allowed' : 'cursor-pointer',
                      )}
                    >
                      <span className={cn(isPremiumLocked && 'opacity-50')}>{tab.label}</span>
                      {isPremiumLocked && (
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger asChild>
                            {/* pointer-events-auto overrides the trigger's own
                             *  disabled:pointer-events-none, which otherwise
                             *  cascades to children and blocks hover here. */}
                            <span
                              data-testid={`catalog-ops-batch-tab-premium-badge-${tab.value}`}
                              className="pointer-events-auto ml-1 inline-flex"
                            >
                              <PremiumBadge size={14} />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-70 text-center">
                            {t(PREMIUM_TAB_TOOLTIP_KEY[tab.value] ?? '')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {!isPremiumLocked && tab.count !== undefined && (
                        <span
                          data-testid={`catalog-ops-batch-tab-count-${tab.value}`}
                          className={cn(
                            'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                            tab.value === activeTab
                              ? 'text-primary bg-white'
                              : 'bg-primary text-white',
                          )}
                        >
                          {String(tab.count)}
                        </span>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {canManageArchive && (
                <ArchiveViewSwitch
                  value={view}
                  onChange={(next) => {
                    setView(next);
                    clearSelection();
                  }}
                  archivedCount={archivedCounts?.all}
                  testId="catalog-ops-batch-archive-view-switch"
                />
              )}
            </div>

            {/* Shared content panel — all tabs share the same layout */}
            {tabsWithCounts.map((tab) => (
              <TabsContent
                key={tab.value}
                data-testid={`catalog-ops-batch-tab-panel-${tab.value}`}
                value={tab.value}
                className="mt-0 p-6"
              >
                {/* Toolbar */}
                <div className="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <p data-testid="catalog-ops-batch-tab-title" className="text-sm font-semibold">
                      {activeTabDef?.label}
                    </p>
                    <p
                      data-testid="catalog-ops-batch-showing-count"
                      className="text-muted-foreground text-xs"
                    >
                      {showTableSkeleton
                        ? 'Loading batches…'
                        : `Showing ${String(rows.length)} of ${String(total)} batches`}
                    </p>
                    {/* Read-only delivering/rejected KPIs — shown only when
                        non-zero so they stay out of the way otherwise. */}
                    {(deliveringCount > 0 || rejectedCount > 0) && (
                      <div className="mt-1.5 flex items-center gap-2">
                        {deliveringCount > 0 && (
                          <span
                            data-testid="catalog-ops-batch-delivering-count-badge"
                            className="bg-stage-active-bg text-stage-active inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums"
                          >
                            {String(deliveringCount)} {t('batchTable.deliveringCount')}
                          </span>
                        )}
                        {rejectedCount > 0 && (
                          <span
                            data-testid="catalog-ops-batch-rejected-count-badge"
                            className="bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums"
                          >
                            {String(rejectedCount)} {t('batchTable.rejectedCount')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {canManageArchive && selectedRows.length > 0 && (
                      <ArchiveBulkActions
                        count={selectedRows.length}
                        archived={view === 'archived'}
                        isPending={jobsBatch.isPending}
                        onClear={clearSelection}
                        testId="catalog-ops-batch-bulk-archive"
                        onConfirm={() => {
                          jobsBatch.run(
                            selectedRows.map((r) => r.id),
                            view !== 'archived',
                          );
                        }}
                      />
                    )}
                    <div className="relative">
                      <Search
                        className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                        aria-hidden="true"
                      />
                      <Input
                        data-testid="catalog-ops-batch-search-input"
                        placeholder="Search by batch ID or name — press Enter"
                        aria-label="Search batches by ID or name"
                        value={searchDraft}
                        disabled={isNativelyEmpty}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearchDraft(value);
                          // Manually clearing the field (backspace/select-all-delete) should
                          // restore the unfiltered list immediately, same as the Clear button.
                          if (value === '') {
                            commitSearch('');
                          }
                        }}
                        onKeyDown={handleSearchKeyDown}
                        className="h-9 min-w-84 pr-9 pl-9 text-sm"
                      />
                      {searchDraft.length > 0 && (
                        <button
                          type="button"
                          aria-label="Clear search"
                          data-testid="catalog-ops-batch-search-clear-button"
                          onClick={handleSearchClear}
                          className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer transition-colors"
                        >
                          <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                    <Button
                      data-testid="catalog-ops-batch-filter-button"
                      variant="outline"
                      size="sm"
                      disabled={isNativelyEmpty}
                      className={cn(
                        'gap-1.5',
                        isNativelyEmpty ? 'cursor-not-allowed' : 'cursor-pointer',
                      )}
                      onClick={() => {
                        setFilterOpen(true);
                      }}
                    >
                      <Funnel className="h-3.5 w-3.5" aria-hidden="true" />
                      Filter
                      {appliedFilterCount > 0 && (
                        <span
                          data-testid="catalog-ops-batch-filter-count-badge"
                          aria-label={`${String(appliedFilterCount)} filters applied`}
                          className="bg-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold text-white tabular-nums"
                        >
                          {String(appliedFilterCount)}
                        </span>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Body — loading / error / table */}
                {showTableSkeleton ? (
                  <div data-testid="catalog-ops-batch-table-loading" className="space-y-2">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full rounded-md" />
                    ))}
                  </div>
                ) : isError ? (
                  <div
                    data-testid="catalog-ops-batch-table-error"
                    className="flex flex-col items-center gap-3 py-12"
                  >
                    <p className="text-muted-foreground text-sm">Failed to load batches.</p>
                    <Button
                      data-testid="catalog-ops-batch-table-retry-button"
                      variant="outline"
                      size="sm"
                      className="cursor-pointer"
                      onClick={() => {
                        void refetch();
                      }}
                    >
                      Retry
                    </Button>
                  </div>
                ) : (
                  <DataTable<BatchRecord>
                    testId="catalog-ops-batch-table"
                    columns={columns}
                    data={rows}
                    rowKey={(r: BatchRecord) => r.id}
                    enableSorting
                    pagination={displayPagination}
                    onPaginationChange={setPagination}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    pageSizeOptions={[10, 20, 50]}
                    emptyMessage={
                      view === 'archived'
                        ? tc('listArchive.empty')
                        : 'No batches match the current filter.'
                    }
                    getRowClassName={getRowClassName}
                    renderSubRow={(row: BatchRecord) => (
                      <BatchChildRuns
                        jobId={row.id}
                        onOpenRun={(runId) => {
                          openChildRun(row.id, runId);
                        }}
                      />
                    )}
                    onRowClick={handleRowClick}
                    tableItemType="batches"
                    // Bulk archive (admins only). Checkboxes reveal on hover and the
                    // selection is page-scoped so the count and the action agree. The
                    // selection is controlled from this component so the bulk controls
                    // render inline in the toolbar row above, beside the search.
                    enableRowSelection={canManageArchive}
                    revealSelectionOnHover
                    clearSelectionOnPageChange
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                  />
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
      <CatalogFilterDrawer
        open={filterOpen}
        onOpenChange={setFilterOpen}
        onApply={(payload) => {
          applyFilters({
            connectorCode: payload.connectorCode,
            useCase: payload.useCase,
            fromDate: payload.fromDate,
            toDate: payload.toDate,
            coverage: payload.coverage,
            source: payload.source,
            appliedFilterCount: payload.activeFilterCount,
          });
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Filter drawer — options
// ---------------------------------------------------------------------------

/** Values mirror the API `useCase` enum sent on `?useCase=` to `/jobs`.
 *  Only Google Ads and Meta Ads are surfaced for now — SEO / AEO, Product Copy,
 *  and Item Setup are hidden until those enrichment types ship. */
const ENRICHMENT_TYPE_OPTIONS: FormSelectOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Google Ads', value: 'GMC_ADS' },
  { label: 'Meta Ads', value: 'META_ADS' },
  // { label: 'SEO / AEO', value: 'SEO_AEO' },
  // { label: 'Product Copy', value: 'PRODUCT_COPY' },
  // { label: 'Item Setup', value: 'ITEM_SETUP' },
];

const DATE_RANGE_OPTIONS: FormSelectOption[] = [
  { label: 'Last 7 Days', value: 'last-7' },
  { label: 'Last 15 Days', value: 'last-15' },
  { label: 'Last 30 Days', value: 'last-30' },
  { label: 'Last 60 Days', value: 'last-60' },
  { label: 'Last 90 Days', value: 'last-90' },
  { label: 'Custom Range', value: 'custom' },
];

/** Connector/Platform options — value mirrors the backend `connectorCode` enum
 *  (passed as-is on the `/jobs` query string). `'all'` sentinel means
 *  "no filter applied" and is stripped before the request. Only Google Merchant
 *  Center and Meta are surfaced for now — Shopify, File, and PIM are hidden
 *  until those connectors ship. */
const PLATFORM_OPTIONS: FormSelectOption[] = [
  { label: 'All Platform', value: 'all' },
  { label: 'Google Merchant Center', value: 'GMC' },
  { label: 'Meta', value: 'META' },
  // { label: 'Shopify', value: 'SHOPIFY' },
  // { label: 'File', value: 'FILE' },
  // { label: 'PIM', value: 'PIM' },
];

/** Values mirror the API `coverage` enum sent on `?coverage=` to `/jobs`. */
const COVERAGE_OPTIONS: FormSelectOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Below 90%', value: 'BELOW_90' },
  { label: '90–95%', value: 'BETWEEN_90_95' },
  { label: '95–100%', value: 'BETWEEN_95_100' },
];

/** Values mirror the API batch trigger `source` enum sent on `?source=` to
 *  `/jobs`. `'all'` sentinel means "no filter" and is stripped before the request. */
const SOURCE_OPTIONS: FormSelectOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Scheduled Jobs', value: 'SCHEDULED' },
  { label: 'Manual Trigger', value: 'SCHEDULED_UX_TRIGGER' },
  { label: 'Trial Workflow', value: 'TRIAL' },
  { label: 'Feed Audit', value: 'FEED_AUDIT' },
];

interface FilterFormValues {
  enrichmentType: string;
  dateRange: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  platform: string;
  needsAttention: boolean;
  coverage: string;
  source: string;
}

const FILTER_DEFAULTS: FilterFormValues = {
  enrichmentType: 'all',
  // Empty string = no date filter; the FormSelect shows its placeholder.
  dateRange: '',
  startDate: undefined,
  endDate: undefined,
  platform: 'all',
  needsAttention: false,
  coverage: 'all',
  source: 'all',
};

// ---------------------------------------------------------------------------
// Filter drawer — accordion section wrapper
// ---------------------------------------------------------------------------

/** Collapsible accordion section for a single filter group. */
function FilterSection({
  label,
  children,
  defaultOpen = true,
  testId,
}: {
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
  /** Automation hook: base `data-testid` for the section and its toggle button. */
  testId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId} className="py-4">
      <button
        type="button"
        data-testid={testId ? `${testId}-toggle` : undefined}
        onClick={() => {
          setOpen(!open);
        }}
        className="flex w-full cursor-pointer items-center justify-between text-sm font-semibold"
      >
        {label}
        {open ? (
          <ChevronUp className="text-muted-foreground h-4 w-4" />
        ) : (
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        )}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter drawer — component
// ---------------------------------------------------------------------------

/** Result payload propagated to the parent when the drawer commits. */
interface CatalogFilterApplyPayload {
  /** Number of non-default selections — drives the Filter-button badge. */
  activeFilterCount: number;
  /** Maps to `?connectorCode=…` on `/jobs`. `undefined` = no filter. */
  connectorCode: ConnectorCodeWire | undefined;
  /** Maps to `?useCase=…` on `/jobs`. `undefined` = no filter. */
  useCase: string | undefined;
  /** Start of date range in `YYYY-MM-DD`. `undefined` = no filter. */
  fromDate: string | undefined;
  /** End of date range in `YYYY-MM-DD`. `undefined` = no filter. */
  toDate: string | undefined;
  /** Maps to `?coverage=…` on `/jobs` (e.g. 'BELOW_90'). `undefined` = no filter. */
  coverage: string | undefined;
  /** Maps to `?source=…` on `/jobs` (e.g. 'TRIAL'). `undefined` = no filter. */
  source: string | undefined;
}

/** Converts the drawer's date-range selection into `YYYY-MM-DD` API params.
 *  Preset options compute bounds relative to today; custom uses the pickers. */
function resolveDateRange(
  dateRange: string,
  startDate: Date | undefined,
  endDate: Date | undefined,
): { fromDate: string | undefined; toDate: string | undefined } {
  const today = todayIsoDate();
  const presets: Record<string, number> = {
    'last-7': -7,
    'last-15': -15,
    'last-30': -30,
    'last-60': -60,
    'last-90': -90,
  };
  if (dateRange in presets) {
    return { fromDate: shiftIsoDate(today, presets[dateRange] ?? 0), toDate: endOfTodayISO() };
  }
  if (dateRange === 'custom') {
    return {
      fromDate: startDate ? jsDateToIsoDate(startDate) : undefined,
      toDate: endDate ? endOfLocalDayISO(endDate) : undefined,
    };
  }
  return { fromDate: undefined, toDate: undefined };
}

/** Right-side filter drawer for the Catalog Operations batch table. */
function CatalogFilterDrawer({
  open,
  onOpenChange,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired on Apply / Reset with the committed filter payload. */
  onApply: (payload: CatalogFilterApplyPayload) => void;
}) {
  const { control, reset, handleSubmit } = useForm<FilterFormValues>({
    defaultValues: FILTER_DEFAULTS,
  });

  const formValues = useWatch({ control });
  const dateRange = formValues.dateRange;

  /** Count of non-default selections shown as the Apply button badge. */
  const activeFilterCount = [
    formValues.enrichmentType !== 'all',
    !!formValues.dateRange,
    formValues.platform !== 'all',
    formValues.coverage !== 'all',
    formValues.source !== 'all',
  ].filter(Boolean).length;

  function onSubmit(data: FilterFormValues) {
    const { fromDate, toDate } = resolveDateRange(data.dateRange, data.startDate, data.endDate);
    onApply({
      activeFilterCount,
      connectorCode: data.platform === 'all' ? undefined : (data.platform as ConnectorCodeWire),
      useCase: data.enrichmentType === 'all' ? undefined : data.enrichmentType,
      fromDate,
      toDate,
      coverage: data.coverage === 'all' ? undefined : data.coverage,
      source: data.source === 'all' ? undefined : data.source,
    });
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        style={{
          top: '1rem',
          bottom: '1rem',
          right: '1rem',
          height: 'auto',
          width: '460px',
          maxWidth: '460px',
        }}
        className="flex flex-col gap-0 rounded-2xl border-0 p-0 shadow-2xl"
        data-testid="catalog-ops-batch-filter-sheet"
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b px-4 py-3 pr-10">
          <Filter className="h-4 w-4 shrink-0" aria-hidden="true" />
          <SheetTitle className="text-base font-semibold">Filters</SheetTitle>
        </div>

        {/* Filter fields */}
        <form
          data-testid="catalog-ops-batch-filter-form"
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="divide-border bg-surface-blue mx-4 my-4 flex-1 divide-y overflow-y-auto rounded-xl px-4">
            <FilterSection
              label="Enrichment Type"
              testId="catalog-ops-batch-filter-enrichment-type"
            >
              <FormSelect
                control={control}
                name="enrichmentType"
                testId="catalog-ops-batch-filter-enrichment-type-select"
                placeholder="All"
                options={ENRICHMENT_TYPE_OPTIONS}
              />
            </FilterSection>

            <FilterSection label="Source" testId="catalog-ops-batch-filter-source">
              <FormSelect
                control={control}
                name="source"
                testId="catalog-ops-batch-filter-source-select"
                placeholder="All"
                options={SOURCE_OPTIONS}
              />
            </FilterSection>

            <FilterSection label="Date Range" testId="catalog-ops-batch-filter-date-range">
              <FormSelect
                control={control}
                name="dateRange"
                testId="catalog-ops-batch-filter-date-range-select"
                placeholder="Select range"
                options={DATE_RANGE_OPTIONS}
              />
              {dateRange === 'custom' && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <FormDatePicker
                    control={control}
                    name="startDate"
                    testId="catalog-ops-batch-filter-start-date-picker"
                    label="From"
                    placeholder="dd/mm/yyyy"
                  />
                  <FormDatePicker
                    control={control}
                    name="endDate"
                    testId="catalog-ops-batch-filter-end-date-picker"
                    label="To"
                    placeholder="dd/mm/yyyy"
                  />
                </div>
              )}
            </FilterSection>

            <FilterSection label="Connector / Platform" testId="catalog-ops-batch-filter-platform">
              <FormSelect
                control={control}
                name="platform"
                testId="catalog-ops-batch-filter-platform-select"
                placeholder="All Platform"
                options={PLATFORM_OPTIONS}
              />
            </FilterSection>

            {/* TODO: Needs Attention filter — to be released in 3.1 */}

            <FilterSection label="Coverage" testId="catalog-ops-batch-filter-coverage">
              <FormSelect
                control={control}
                name="coverage"
                testId="catalog-ops-batch-filter-coverage-select"
                placeholder="All"
                options={COVERAGE_OPTIONS}
              />
            </FilterSection>
          </div>

          {/* Footer */}
          <div className="flex gap-3 border-t px-4 py-4">
            <Button
              type="button"
              variant="outline"
              data-testid="catalog-ops-batch-filter-reset-button"
              onClick={() => {
                reset(FILTER_DEFAULTS);
                onApply({
                  activeFilterCount: 0,
                  connectorCode: undefined,
                  useCase: undefined,
                  fromDate: undefined,
                  toDate: undefined,
                  coverage: undefined,
                  source: undefined,
                });
              }}
              className="h-11 flex-1 cursor-pointer rounded-xl bg-white text-sm font-semibold"
            >
              Reset Filters
            </Button>
            <Button
              type="submit"
              data-testid="catalog-ops-batch-filter-apply-button"
              className="h-11 flex-1 cursor-pointer gap-2 rounded-xl text-sm font-semibold"
            >
              Apply Filters
              {activeFilterCount > 0 && (
                <span className="text-primary inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-lg bg-white px-1.5 text-xs font-bold tabular-nums">
                  {String(activeFilterCount)}
                </span>
              )}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

/** Who started the batch. A scheduled run's worker records `WORKFLOW-SERVICE`, shown as the schedule. */
function CreatedByCell({ row }: { row: BatchRecord }) {
  const scheduled =
    row.source === 'SCHEDULED' &&
    (!row.createdBy || row.createdBy.toUpperCase() === 'WORKFLOW-SERVICE');
  if (scheduled) {
    return (
      <span data-testid={`catalog-ops-batch-created-by-${row.id}`} className="text-sm">
        Schedule
      </span>
    );
  }
  return (
    <ActorStringLabel
      value={row.createdBy}
      showAvatar={false}
      className="text-sm"
      testId={`catalog-ops-batch-created-by-${row.id}`}
    />
  );
}

/**
 * What the last person did to the batch and who ("Approved · ana@…"), with when below; "—" when
 * nobody has acted. An unlabelled action (`OTHER`, or an older backend) shows the actor alone.
 */
function LastActionCell({ row }: { row: BatchRecord }) {
  const { t: tc } = useTranslation('common');
  const labelKey = lastActionLabelKey(row.lastActionType);
  if (!row.lastActionBy && !labelKey) {
    return (
      <span
        data-testid={`catalog-ops-batch-last-action-${row.id}`}
        className="text-muted-foreground text-sm"
      >
        —
      </span>
    );
  }
  return (
    <span
      data-testid={`catalog-ops-batch-last-action-${row.id}`}
      className="flex min-w-0 flex-col gap-0.5"
    >
      <span className="flex min-w-0 flex-wrap items-center gap-1 text-sm">
        {labelKey && (
          <span data-testid={`catalog-ops-batch-last-action-type-${row.id}`}>
            {tc(labelKey)}
            {row.lastActionBy ? ' ·' : ''}
          </span>
        )}
        {row.lastActionBy && (
          <ActorStringLabel value={row.lastActionBy} showAvatar={false} className="text-sm" />
        )}
      </span>
      {row.lastActionAt && (
        <span className="text-muted-foreground text-xs">{formatRelative(row.lastActionAt)}</span>
      )}
    </span>
  );
}
